import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { membersAPI, authAPI } from '../services/api';
import awtfLogo from '../assets/awtf_logo.png';

function LogoutIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 8.25 19.5 12m0 0-3.75 3.75M19.5 12H9.75" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 4.5h-3A1.5 1.5 0 0 0 6 6v12a1.5 1.5 0 0 0 1.5 1.5h3" />
    </svg>
  );
}

export default function Navbar() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [picture, setPicture] = useState(null);

  const displayName = user?.full_name || user?.name || '';
  const initial     = displayName[0]?.toUpperCase() ?? 'A';
  const MANAGER_ROLES = ['admin', 'finance', 'board'];
  const profilePath = user ? '/profile' : null;

  useEffect(() => {
    if (!user) return;
    if (user.role === 'member') {
      membersAPI.me()
        .then(m => { if (m?.picture) setPicture(m.picture); })
        .catch(() => {});
    } else if (MANAGER_ROLES.includes(user.role)) {
      authAPI.profile()
        .then(p => { if (p?.picture) setPicture(p.picture); })
        .catch(() => {});
    }
  }, [user]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const Avatar = (
    <div className="h-8 w-8 lg:h-10 lg:w-10 rounded-full overflow-hidden ring-2 ring-orange-200/80 shadow-sm bg-gradient-to-br from-orange-500 to-[#1b2857] flex items-center justify-center shrink-0">
      {picture ? (
        <img src={picture} alt={displayName} className="h-full w-full object-cover" onError={() => setPicture(null)} />
      ) : (
        <span className="text-sm lg:text-lg font-bold text-white">{initial}</span>
      )}
    </div>
  );

  return (
    <header className="relative z-40 border-b border-white/40 bg-white/80 backdrop-blur-xl shadow-[0_4px_24px_rgba(15,23,42,0.08)]">
      <div className="mx-auto max-w-[1600px]">
        <div className="flex items-center justify-between gap-3 bg-[radial-gradient(circle_at_top_left,rgba(244,121,32,0.10),transparent_40%),linear-gradient(135deg,rgba(255,255,255,0.97),rgba(255,247,237,0.94))] px-4 py-2.5 lg:px-6 lg:py-3">

          {/* Left: logo + title */}
          <div className="flex items-center gap-2.5">
            <Link to="/"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white p-1 shadow-[0_6px_16px_rgba(244,121,32,0.25)] ring-1 ring-orange-100 lg:h-11 lg:w-11 lg:rounded-2xl">
              <img src={awtfLogo} alt="AWTF Logo" className="h-full w-full object-contain" />
            </Link>
            <div className="min-w-0">
              <Link to="/" className="block text-sm font-bold tracking-tight text-slate-900 lg:text-base">
                AWTF Member Portal
              </Link>
              <p className="hidden text-[11px] text-slate-500 lg:block">Advocates Welfare Trust Fund</p>
            </div>
          </div>

          {/* Right: avatar + name + logout */}
          {user && (
            <div className="flex items-center gap-2">

              {profilePath ? (
                <Link to={profilePath} title="My Profile">
                  {Avatar}
                </Link>
              ) : Avatar}

              {/* Name + role — hidden on mobile */}
              <div className="hidden rounded-xl border border-slate-200/80 bg-white/70 px-3 py-1.5 text-right shadow-sm sm:block">
                <p className="text-sm font-semibold text-slate-900 leading-tight">{displayName}</p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{user.role}</p>
              </div>

              <button onClick={handleLogout}
                className="inline-flex items-center gap-1.5 rounded-xl bg-[#1b2857] px-3 py-2 text-xs font-semibold text-white shadow-[0_6px_16px_rgba(27,40,87,0.22)] transition-transform hover:-translate-y-0.5 hover:bg-[#232f6a] lg:px-4 lg:py-2.5 lg:text-sm">
                <LogoutIcon />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
